'use client';

import React, { createContext, useContext, ReactNode } from 'react';
import { WebsiteSettingsResponse } from './api';

interface WebsiteSettingsContextType {
  settings: WebsiteSettingsResponse | null;
  isLoading: boolean;
}

const WebsiteSettingsContext = createContext<WebsiteSettingsContextType | undefined>(undefined);

interface WebsiteSettingsProviderProps {
  children: ReactNode;
  settings: WebsiteSettingsResponse | null;
  isLoading: boolean;
}

export function WebsiteSettingsProvider({ children, settings, isLoading }: WebsiteSettingsProviderProps) {
  return (
    <WebsiteSettingsContext.Provider value={{ settings, isLoading }}>
      {children}
    </WebsiteSettingsContext.Provider>
  );
}

export function useWebsiteSettings() {
  const context = useContext(WebsiteSettingsContext);
  if (context === undefined) {
    throw new Error('useWebsiteSettings must be used within a WebsiteSettingsProvider');
  }
  return context;
}

// Helper functions to get specific settings
export function useSettingValue(key: string): boolean | string | number | null {
  const { settings } = useWebsiteSettings();
  return settings?.data[key]?.value ?? null;
}

export function useBooleanSetting(key: string): boolean {
  const value = useSettingValue(key);
  return typeof value === 'boolean' ? value : false;
}

export function useStringSetting(key: string): string {
  const value = useSettingValue(key);
  return typeof value === 'string' ? value : '';
}

export function useNumberSetting(key: string): number {
  const value = useSettingValue(key);
  return typeof value === 'number' ? value : 0;
}
